import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Plus } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SectionLabel } from "../components/SectionLabel";

const BULLETS = [
  "Inline, peninsula and island exhibits",
  "Custom product displays and hero props",
  "Graphics, lighting, flooring and installation",
];

export default function FeaturedShow() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section ref={ref} data-testid="featured-tradeshow-section" className="scroll-mt-20 bg-panel">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-4 py-24 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8 lg:py-36">
        <Reveal className="lg:col-span-7" y={40}>
          <div className="relative overflow-hidden">
            <span className="absolute left-3 top-3 z-10 h-5 w-5 border-l-2 border-t-2 border-acid" aria-hidden="true" />
            <span className="absolute right-3 top-3 z-10 h-5 w-5 border-r-2 border-t-2 border-acid" aria-hidden="true" />
            <span className="absolute bottom-3 left-3 z-10 h-5 w-5 border-b-2 border-l-2 border-acid" aria-hidden="true" />
            <span className="absolute bottom-3 right-3 z-10 h-5 w-5 border-b-2 border-r-2 border-acid" aria-hidden="true" />
            <motion.img
              data-testid="featured-tradeshow-image"
              src="/images/featured-real.jpg"
              alt="The 10×20 booth Triple O built for Bosky Genetics at the Network Show 2026 — mural walls, display case, wood slat wall and custom soffit"
              className="aspect-[4/3] w-full object-cover"
              style={reduce ? {} : { y: imgY, scale: 1.12 }}
              loading="lazy"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />
            <p className="absolute bottom-5 left-6 font-mono text-[10px] uppercase tracking-[0.3em] text-paper/70">
              Our 10×20 for Bosky Genetics — Network Show 2026, AltaSea
            </p>
          </div>
        </Reveal>

        <div className="flex flex-col justify-center lg:col-span-5">
          <Reveal>
            <SectionLabel index="CH.05">Trade Show Booths</SectionLabel>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-8 font-display text-3xl font-extrabold uppercase leading-[1.12] tracking-tight text-paper sm:text-5xl sm:leading-[1.08]">
              Make the
              <br />
              show floor
              <br />
              <span className="text-acid">stop.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-base leading-relaxed text-fog sm:text-lg">
              This is what we build for you. A booth is not a decorated box — it is a temporary
              flagship, designed, fabricated, wired and installed by one team. Like this 10×20 for
              Bosky Genetics: structural wood walls, a 3D skull with halo lighting, and a TV sunk
              straight into the mural.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <ul className="mt-8 space-y-4">
              {BULLETS.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-paper/85 sm:text-base">
                  <Plus className="mt-1 h-4 w-4 shrink-0 text-acid" aria-hidden="true" />
                  {b}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.4}>
            <a
              href="#contact"
              data-testid="featured-tradeshow-cta"
              className="group mt-10 flex w-fit items-center gap-3 border border-acid px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-acid transition-colors duration-300 hover:bg-acid hover:text-ink"
            >
              Plan Your Next Show
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
